import React, { useMemo, useState } from 'react';
import { Bell } from 'lucide-react';
import { Lock } from 'phosphor-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import { formatNative } from './optionsDashboardUtils';
import {
  formatExpirationDate,
  formatFeesDisplay,
  formatOpenedDate,
  formatOperationPnl,
  getDirectionalBiasLabel,
  getDteTone,
  sortLegsForDisplay,
} from './optionsOpenPositionsUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsCampaignHistorySection } from './OptionsCampaignHistorySection';
import { OptionsLegGreeksGrid } from './OptionsLegGreeksGrid';
import { OptionsMonetaryText } from './OptionsMonetaryText';
import { formatOperationDirectionalBiasLabel } from './optionsStrategyBiasUtils';
import { OptionsUnderlyingStrategyTitle } from './OptionsUnderlyingStrategyTitle';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { formatLocalizedNumber } from './optionsLocaleFormatting';
import { formatSignedNative } from './optionsPortfolioRiskUtils';

const getDeltaToneClass = (value) => {
  if (value === null || value === undefined) return 'text-white/45';
  const numeric = Number(value);
  if (numeric > 0) return 'text-[#4ADE80]';
  if (numeric < 0) return 'text-[#F87171]';
  return 'text-white/70';
};

export const OptionsOpenPositionCard = ({ item, index, selectedCurrency, alertCount = 0, onAlertClick = null }) => {
  const { t, formatMessage, isPortuguese } = useOptionsLocale();
  const [expanded, setExpanded] = useState(false);
  const { isPro, openUpgradeModal } = useOptionsEntitlements();

  const handleAccordionChange = (value) => {
    if (!isPro) {
      openUpgradeModal("POSITION_DETAILS");
      return;
    }
    setExpanded(Boolean(value));
  };

  const dteTone = getDteTone(item.dte);
  const expirationLabel = useMemo(() => formatExpirationDate(item.expiration, isPortuguese ? 'pt' : 'en'), [item.expiration, isPortuguese]);
  const operationPnl = formatOperationPnl({
    selectedCurrency,
    nativeValue: item.netPnlNative,
    usdValue: item.netPnlUsd,
    underlying: item.underlying,
  });
  const campaignPnl = item.campaignContext
    ? formatOperationPnl({
        selectedCurrency,
        nativeValue: item.campaignContext.campaignNetBase,
        usdValue: item.campaignContext.campaignNetUsd,
        underlying: item.underlying,
      })
    : null;
  const feesDisplay = formatFeesDisplay({
    selectedCurrency,
    nativeValue: item.feesNative,
    usdValue: item.feesUsd,
    underlying: item.underlying,
  });
  const directionalBias = getDirectionalBiasLabel(item.operation);
  const activeLegIdSet = useMemo(() => new Set(item.operation?.active_leg_ids || []), [item.operation]);
  const hasRollMaterialization = activeLegIdSet.size > 0;
  const orderedLegs = useMemo(() => sortLegsForDisplay(item.legs), [item.legs]);
  const activeLegsForDisplay = useMemo(
    () => orderedLegs.filter((leg) => leg.status === 'OPEN'),
    [orderedLegs]
  );
  const rollHistory = item.operation?.roll_history || [];
  const directionalBiasLabel = useMemo(() => formatOperationDirectionalBiasLabel(item.operation, t), [item.operation, t]);
  const positionNetDelta = useMemo(() => {
    const values = activeLegsForDisplay
      .map((leg) => leg?.current_delta)
      .filter((value) => value !== null && value !== undefined);
    if (!values.length) return null;
    return values.reduce((sum, value) => sum + Number(value), 0);
  }, [activeLegsForDisplay]);

  return (
    <Card className="border border-white/5 bg-zinc-950/40 backdrop-blur-md hover:bg-zinc-950/60 hover:border-white/10 transition-all duration-300 rounded-[24px] text-white shadow-none" data-testid={`options-open-position-card-${index}`}>
      <CardContent className="p-6 sm:p-7">
        <Accordion type="single" collapsible value={expanded ? item.id : ''} onValueChange={handleAccordionChange}>
          <AccordionItem value={item.id} className="border-none" data-testid={`options-open-position-accordion-${index}`}>
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <OptionsUnderlyingStrategyTitle
                  as="h2"
                  underlying={item.underlying}
                  strategy={item.strategyLabel}
                  iconSize={18}
                  className="text-lg font-black font-heading text-white sm:text-xl"
                  testId={`options-open-position-strategy-${index}`}
                  suffixNode={
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        positionNetDelta === null 
                          ? 'bg-white/5 text-white/40 border-white/10' 
                          : positionNetDelta > 0 
                            ? 'bg-[#4ADE80]/10 text-[#4ADE80] border-[#4ADE80]/20' 
                            : positionNetDelta < 0 
                              ? 'bg-[#F87171]/10 text-[#F87171] border-[#F87171]/20' 
                              : 'bg-white/5 text-white/70 border-white/10'
                      } tabular-nums`}
                      data-testid={`options-open-position-net-delta-${index}`}
                    >
                      Δ {positionNetDelta === null ? '—' : formatSignedNative(positionNetDelta, item.underlying, 4)}
                    </span>
                  }
                />
                {onAlertClick ? (
                  <Button type="button" variant="ghost" size="icon" className={`h-8 w-8 shrink-0 rounded-full border ${alertCount ? 'border-[#F0B90B]/35 bg-[#F0B90B]/10 text-[#F0B90B]' : 'border-white/5 bg-white/[0.02] text-white/55'} hover:bg-white/10 hover:text-white`} onClick={() => onAlertClick(item)} data-testid={`options-open-position-alerts-${index}`}>
                    <Bell size={15} />
                    {alertCount ? <span className="sr-only">{alertCount}</span> : null}
                  </Button>
                ) : null}
              </div>

              <div className="grid gap-x-6 gap-y-4 border-y border-white/5 py-4 sm:grid-cols-[minmax(190px,1.8fr)_repeat(4,minmax(88px,0.72fr))] sm:items-center" data-testid={`options-open-position-summary-grid-${index}`}>
                <CompactMetric label={isPortuguese ? 'Posição' : 'Position'} valueClassName="text-white" testId={`options-open-position-position-summary-${index}`}>
                  <CompactPositionSummary 
                    legs={activeLegsForDisplay} 
                    underlying={item.underlying} 
                    testId={`options-open-position-position-summary-${index}-value`} 
                    operationQuantity={item.operation?.quantity}
                  />
                </CompactMetric>
                <CompactMetric label="DTE" value={item.dte !== null && item.dte !== undefined ? `${item.dte} DTE` : '—'} valueClassName={dteTone.valueClassName} testId={`options-open-position-dte-${index}`} />
                <CompactMetric label={t('positions.expiryLabel')} value={expirationLabel} testId={`options-open-position-expiry-${index}`} />
                <CompactMetric label={t('positions.pnlLabel')} value={operationPnl.primary} valueClassName={operationPnl.colorClassName} monetary testId={`options-open-position-pnl-${index}`} />
                <CompactMetric label={t('dashboard.fees')} value={feesDisplay.primary} valueClassName="text-white" monetary testId={`options-open-position-fees-${index}`} />
              </div>

              <div className="flex flex-col gap-3 text-xs text-white/55 sm:flex-row sm:items-center sm:justify-between" data-testid={`options-open-position-secondary-${index}`}>
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  <span data-testid={`options-open-position-opened-at-${index}`}>{t('positions.opened')} {formatOpenedDate(item.openedAt)}</span>
                  {item.campaignContext ? <><span className="text-white/25">•</span><span data-testid={`options-open-position-cycle-meta-${index}`}>{formatMessage(t('positions.campaignOperations'), { count: item.campaignContext.operationCount })}</span></> : null}
                  {directionalBias ? <><span className="text-white/25">•</span><span data-testid={`options-open-position-directional-bias-${index}`}>{directionalBiasLabel}</span></> : null}
                </div>
                <AccordionTrigger 
                  className="h-auto w-auto shrink-0 p-0 text-xs font-bold text-white/70 hover:text-[#00E676] transition-colors duration-200 no-underline hover:no-underline [&[data-state=open]>svg]:rotate-180" 
                  data-testid={`options-open-position-expand-${index}`}
                  onClick={(e) => {
                    if (!isPro) {
                      e.preventDefault();
                      e.stopPropagation();
                      openUpgradeModal("POSITION_DETAILS");
                    }
                  }}
                >
                  <span className="flex items-center gap-1.5">
                    {!isPro && <Lock size={12} className="text-[#A855F7]" weight="fill" />}
                    {expanded ? t('positions.hideDetails') : t('positions.viewDetails')}
                    {!isPro && <span className="text-[10px] bg-[#A855F7]/10 text-[#A855F7] px-1.5 py-0.5 rounded-full font-bold">PRO</span>}
                  </span>
                </AccordionTrigger>
              </div>
            </div>

            <AccordionContent className="pt-6" data-testid={`options-open-position-details-${index}`}>
              <div className="space-y-6 rounded-[20px] border border-white/5 bg-zinc-950/20 p-5 sm:p-6 backdrop-blur-sm">

                {/* 1. LEGS ATIVAS */}
                {activeLegsForDisplay.length > 0 ? (
                  <div className="space-y-3" data-testid={`options-open-position-active-legs-${index}`}>
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45">
                      {isPortuguese ? 'LEGS ATIVAS' : 'ACTIVE LEGS'}
                    </p>
                    <div className="grid gap-3 lg:grid-cols-2">
                      {activeLegsForDisplay.map((leg, legIdx) => (
                        <ActiveLegCard key={leg.id} leg={leg} selectedCurrency={selectedCurrency} positionIndex={index} legIndex={legIdx} />
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* 2. ROLAGENS */}
                {rollHistory.length > 0 ? (
                  <div className="space-y-3" data-testid={`options-open-position-rolls-${index}`}>
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45">
                      {rollHistory.length > 1 ? (isPortuguese ? 'ROLAGENS' : 'ROLLS') : (isPortuguese ? 'ROLAGEM' : 'ROLL')}
                    </p>
                    {rollHistory.length > 1 ? <RollChainSummary rollHistory={rollHistory} /> : null}
                    <div className="grid gap-3 lg:grid-cols-2">
                      {rollHistory.map((entry, rollIdx) => {
                        const resultValue = Number(entry.realized_pnl_base ?? entry.realized_pnl_usd ?? 0);

                        let badgeLabel = "";
                        let badgeClassName = "";
                        if (resultValue > 0) {
                          badgeLabel = isPortuguese ? "ROLAGEM COM LUCRO" : "ROLLED WITH PROFIT";
                          badgeClassName = "border-[#4ADE80]/20 bg-[#4ADE80]/12 text-[#BDFACD]";
                        } else if (resultValue < 0) {
                          badgeLabel = isPortuguese ? "ROLAGEM COM PREJUÍZO" : "ROLLED WITH LOSS";
                          badgeClassName = "border-[#EF4444]/20 bg-[#EF4444]/12 text-[#FFD2D2]";
                        } else {
                          badgeLabel = isPortuguese ? "ROLAGEM NEUTRA" : "NEUTRAL ROLL";
                          badgeClassName = "border-white/10 bg-white/[0.05] text-white";
                        }

                        const fromLabel = `${entry.from_option_type} ${formatLocalizedNumber(Number(entry.from_strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                        const toLabel = `${entry.to_option_type} ${formatLocalizedNumber(Number(entry.to_strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

                        const resultDisplay = formatOperationPnl({
                          selectedCurrency,
                          nativeValue: entry.realized_pnl_base,
                          usdValue: entry.realized_pnl_usd,
                          underlying: item.underlying,
                        });

                        const feesDisplay = formatFeesDisplay({
                          selectedCurrency,
                          nativeValue: entry.fees_base,
                          usdValue: entry.fees_usd,
                          underlying: item.underlying,
                        });

                        return (
                          <div key={rollIdx} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid={`options-open-position-roll-history-card-${index}-${rollIdx}`}>
                            <div className="flex items-center justify-between gap-2">
                              <Badge className={`${badgeClassName} uppercase text-[10px] tracking-wider`} data-testid={`options-open-position-roll-history-classification-${index}-${rollIdx}`}>
                                {badgeLabel}
                              </Badge>
                              <span className="text-[11px] text-white/50" data-testid={`options-open-position-roll-history-date-${index}-${rollIdx}`}>
                                {formatOpenedDate(entry.closed_at)}
                              </span>
                            </div>
                            <div>
                              <p className="text-base font-bold text-white tabular-nums" data-testid={`options-open-position-roll-history-title-${index}-${rollIdx}`}>
                                {fromLabel} <span className="mx-1.5 text-white/35">→</span> {toLabel}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/60">
                              <span data-testid={`options-open-position-roll-history-result-${index}-${rollIdx}`}>
                                {isPortuguese ? 'Resultado' : 'Result'}: <span className={`font-semibold ${resultDisplay.colorClassName}`}><OptionsMonetaryText value={resultDisplay.primary} iconSize={13} /></span>
                              </span>
                              <span data-testid={`options-open-position-roll-history-fees-${index}-${rollIdx}`}>
                                {isPortuguese ? 'Taxas' : 'Fees'}: <span className="font-semibold text-white/80"><OptionsMonetaryText value={feesDisplay.primary} iconSize={13} /></span>
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {/* 3. LEGS REALIZADAS */}
                {orderedLegs.filter(leg => leg.status !== 'OPEN').length > 0 ? (
                  <div className="space-y-3" data-testid={`options-open-position-closed-legs-${index}`}>
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45">
                      {isPortuguese ? 'LEGS REALIZADAS' : 'REALIZED LEGS'}
                    </p>
                    <div className="grid gap-3 lg:grid-cols-2">
                      {orderedLegs.filter(leg => leg.status !== 'OPEN').map((leg, legIdx) => (
                        <ClosedLegCard key={leg.id} leg={leg} selectedCurrency={selectedCurrency} positionIndex={index} legIndex={legIdx} />
                      ))}
                    </div>
                  </div>
                ) : null}

              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export const CompactMetric = ({ label, value, valueClassName = 'text-white', monetary = false, testId, children = null }) => {
  return (
    <div className="min-w-0 md:border-r md:border-white/5 md:last:border-0 md:pr-4" data-testid={testId}>
      <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/35 leading-none mb-1.5">{label}</p>
      {children || (
        <p className={`break-words text-sm sm:text-base font-bold tabular-nums tracking-wide ${valueClassName}`} data-testid={`${testId}-value`}>
          {monetary ? <OptionsMonetaryText value={value} iconSize={14} /> : value}
        </p>
      )}
    </div>
  );
};

export const CompactPositionSummary = ({ legs = [], underlying, testId, operationQuantity = null }) => {
  if (!legs.length && operationQuantity === null) return <p className="mt-1 text-sm font-semibold text-white" data-testid={testId}>—</p>;

  let quantityVal = null;
  if (operationQuantity !== null && Number(operationQuantity) > 0) {
    quantityVal = operationQuantity;
  } else {
    // Busca a primeira leg que tenha uma quantidade aberta original válida
    const validLegWithOpenQty = legs.find((leg) => Number(leg.quantity_opened) > 0);
    if (validLegWithOpenQty) {
      quantityVal = validLegWithOpenQty.quantity_opened;
    } else {
      quantityVal = legs[0]?.quantity_remaining ?? legs[0]?.quantity_opened;
    }
  }

  const quantity = formatLegQuantity(quantityVal);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold tabular-nums text-white" data-testid={testId}>
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        {legs.map((leg, legIndex) => (
          <React.Fragment key={leg.id || `${leg.position_side}-${leg.option_type}-${leg.strike}-${legIndex}`}>
            {legIndex ? <span className="text-white/35">/</span> : null}
            <span>{formatLocalizedNumber(Number(leg.strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/55">{leg.option_type || 'OPTION'}</span>
          </React.Fragment>
        ))}
      </span>
      <span className="text-white/30">·</span>
      <span className="inline-flex items-center gap-1.5 text-white/80" data-testid={`${testId}-quantity`}>
        <OptionsAssetIcon assetKey={underlying} size={14} className="shrink-0" />
        <span>{quantity}</span>
      </span>
    </div>
  );
};

export const formatLegQuantity = (value) => {
  if (value === null || value === undefined) return '—';
  return formatLocalizedNumber(Number(value), {
    minimumFractionDigits: Number.isInteger(Number(value)) ? 0 : 2,
    maximumFractionDigits: 4,
  });
};

export const RollChainSummary = ({ rollHistory }) => {
  const chain = [];
  rollHistory.forEach((entry, idx) => {
    if (idx === 0) {
      chain.push(`${entry.from_option_type} ${formatLocalizedNumber(Number(entry.from_strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);
    }
    chain.push(`${entry.to_option_type} ${formatLocalizedNumber(Number(entry.to_strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);
  });
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-sm">
      {chain.map((step, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <span className="mx-0.5 text-white/35">→</span> : null}
          <span className="font-semibold tabular-nums text-white/75">{step}</span>
        </React.Fragment>
      ))}
    </div>
  );
};

export const ClosedLegCard = ({ leg, selectedCurrency, positionIndex, legIndex }) => {
  const { isPortuguese } = useOptionsLocale();
  const realizedDisplay = formatOperationPnl({
    selectedCurrency,
    nativeValue: leg.realized_pnl_base,
    usdValue: leg.realized_pnl_usd,
    underlying: leg.underlying,
  });
  const feesDisplay = formatFeesDisplay({
    selectedCurrency,
    nativeValue: leg.total_fees_base,
    usdValue: leg.total_fees_usd,
    underlying: leg.underlying,
  });

  return (
    <div className="rounded-[16px] border border-white/5 bg-zinc-900/30 hover:bg-zinc-900/50 hover:border-white/10 transition-all duration-300 p-4" data-testid={`options-open-position-leg-card-closed-${positionIndex}-${legIndex}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm sm:text-base font-bold text-white tracking-wide" data-testid={`options-open-position-leg-type-${positionIndex}-${legIndex}`}>
            {leg.position_side} {leg.option_type} {formatLocalizedNumber(Number(leg.strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </p>
          <p className="mt-1 text-xs text-white/40" data-testid={`options-open-position-leg-expiration-${positionIndex}-${legIndex}`}>
            {formatExpirationDate(leg.expiration, isPortuguese ? 'pt' : 'en')}
          </p>
        </div>
        <Badge className="border-white/10 bg-white/5 text-white/50 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm" data-testid={`options-open-position-leg-status-${positionIndex}-${legIndex}`}>
          CLOSED
        </Badge>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="rounded-[12px] border border-white/5 bg-zinc-950/30 px-3.5 py-2.5">
          <p className="text-[9px] font-medium uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">
            {isPortuguese ? 'P&L LÍQUIDO REALIZADO' : 'NET REALIZED P&L'}
          </p>
          <p className={`text-xs sm:text-sm font-bold tabular-nums ${realizedDisplay.colorClassName}`} data-testid={`options-open-position-leg-realized-${positionIndex}-${legIndex}`}>
            <OptionsMonetaryText value={realizedDisplay.primary} iconSize={13} />
          </p>
        </div>
        <div className="rounded-[12px] border border-white/5 bg-zinc-950/30 px-3.5 py-2.5">
          <p className="text-[9px] font-medium uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">
            {isPortuguese ? 'TAXAS' : 'FEES'}
          </p>
          <p className="text-xs sm:text-sm font-bold text-white/80 tabular-nums" data-testid={`options-open-position-leg-fees-${positionIndex}-${legIndex}`}>
            <OptionsMonetaryText value={feesDisplay.primary} iconSize={13} />
          </p>
        </div>
      </div>
    </div>
  );
};

export const ActiveLegCard = ({ leg, selectedCurrency, positionIndex, legIndex }) => {
  const { t, isPortuguese } = useOptionsLocale();
  const [greeksExpanded, setGreeksExpanded] = useState(false);

  const pnlDisplay = formatOperationPnl({
    selectedCurrency,
    nativeValue: leg.net_pnl_base,
    usdValue: leg.net_pnl_usd,
    underlying: leg.underlying,
  });

  return (
    <div className="rounded-[16px] border border-white/5 bg-zinc-900/30 hover:bg-zinc-900/50 hover:border-white/10 transition-all duration-300 p-4 space-y-3" data-testid={`options-open-position-leg-card-active-${positionIndex}-${legIndex}`}>
      <div 
        className="flex items-center justify-between gap-3 cursor-pointer select-none"
        onClick={() => setGreeksExpanded(!greeksExpanded)}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <p className="text-sm sm:text-base font-bold text-white tracking-wide" data-testid={`options-open-position-leg-type-${positionIndex}-${legIndex}`}>
              {leg.position_side} {leg.option_type} {formatLocalizedNumber(Number(leg.strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
            <Badge className="border-[#4ADE80]/20 bg-[#4ADE80]/8 text-[#4ADE80] text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm">
              OPEN
            </Badge>
          </div>
          <p className="mt-1 text-xs text-white/40">
            {formatExpirationDate(leg.expiration, isPortuguese ? 'pt' : 'en')}
          </p>
        </div>

        <div className="flex items-center gap-4 text-right">
          <div className="text-xs">
            <p className="text-white/35 uppercase tracking-[0.12em] text-[9px] font-semibold mb-0.5">Qtd.</p>
            <p className="font-bold text-white/85 text-xs sm:text-sm" data-testid={`options-open-position-leg-quantity-${positionIndex}-${legIndex}-value`}>
              {formatLegQuantity(leg.quantity_remaining ?? leg.quantity_opened)}
            </p>
          </div>
          <div className="text-xs">
            <p className="text-white/35 uppercase tracking-[0.12em] text-[9px] font-semibold mb-0.5">P&L</p>
            <p className={`font-bold tabular-nums text-xs sm:text-sm ${pnlDisplay.colorClassName}`} data-testid={`options-open-position-leg-net-${positionIndex}-${legIndex}-value`}>
              <OptionsMonetaryText value={pnlDisplay.primary} iconSize={13} />
            </p>
          </div>
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/5 bg-white/[0.02] text-white/50 hover:text-white hover:border-white/10 transition-colors duration-200">
            {greeksExpanded ? '∧' : '∨'}
          </span>
        </div>
      </div>

      {greeksExpanded ? (
        <div className="border-t border-white/5 pt-3" data-testid={`options-open-position-leg-greeks-${positionIndex}-${legIndex}`}>
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/35 mb-2">
            {isPortuguese ? 'GREGAS ATUAIS' : 'CURRENT GREEKS'}
          </p>
          <OptionsLegGreeksGrid leg={leg} testIdPrefix={`options-open-position-leg-greeks-${positionIndex}-${legIndex}`} />
        </div>
      ) : null}
    </div>
  );
};

const InlineMetric = ({ label, value, secondary = null, valueClassName = 'text-white', testId }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</p>
    <p className={`mt-1 text-sm font-semibold tabular-nums ${valueClassName}`} data-testid={`${testId}-value`}><OptionsMonetaryText value={value} iconSize={14} /></p>
    {secondary ? <p className="mt-1 text-xs text-white/50 tabular-nums" data-testid={`${testId}-secondary`}><OptionsMonetaryText value={secondary} iconSize={12} /></p> : null}
  </div>
);

export default OptionsOpenPositionCard;